import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { authAPI } from '../utils/api';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    firma: '',
    passwort: '',
    passwortWiederholen: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (formData.passwort !== formData.passwortWiederholen) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    try {
      const result = await authAPI.registerPublic({
        vorname: formData.vorname,
        nachname: formData.nachname,
        email: formData.email,
        telefon: formData.telefon,
        firma: formData.firma,
        passwort: formData.passwort
      });
      
      if (result.success) {
        // User muss erst vom Admin freigeschaltet werden
        setSuccess(result.message || 'Registrierung erfolgreich! Ihr Konto muss noch vom Administrator freigeschaltet werden.');
        // Nach 3 Sekunden zur Login-Seite
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Register Card */}
      <div className="relative z-10 w-full max-w-2xl mx-4">
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl p-12 border border-white">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/logo-new.png" 
              alt="Autogutachter24" 
              className="h-20 w-auto mx-auto mb-4"
            />
            <p className="text-gray-500 mt-2">KFZ-Sachverständigen Software</p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Registrierung</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="text"
                name="vorname"
                placeholder="Vorname"
                value={formData.vorname}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12 bg-white/50 border-gray-200"
              />
              <Input
                type="text"
                name="nachname"
                placeholder="Nachname"
                value={formData.nachname}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12 bg-white/50 border-gray-200"
              />
            </div>

            <Input
              type="email"
              name="email"
              placeholder="E-Mail"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              className="h-11 px-4 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <Input
              type="tel"
              name="telefon"
              placeholder="Telefon"
              value={formData.telefon}
              onChange={handleChange}
              disabled={loading}
              className="h-11 px-4 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <Input
              type="text"
              name="firma"
              placeholder="Firma"
              value={formData.firma}
              onChange={handleChange}
              required
              disabled={loading}
              className="h-11 px-4 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <Input
              type="password"
              name="passwort"
              placeholder="Passwort"
              value={formData.passwort}
              onChange={handleChange}
              required
              disabled={loading}
              className="h-11 px-4 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <Input
              type="password"
              name="passwortWiederholen"
              placeholder="Passwort wiederholen"
              value={formData.passwortWiederholen}
              onChange={handleChange}
              required
              disabled={loading}
              className="h-11 px-4 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded">
                {success}
              </div>
            )}

            <Button 
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-medium transition-all shadow-lg"
            >
              {loading ? 'Registrieren...' : 'Registrieren'}
            </Button>

            {/* Login-Link */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Bereits registriert?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-blue-600 hover:text-blue-700 font-semibold hover:underline"
                >
                  Jetzt anmelden
                </button>
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          © 2024 Autogutachter24 - Alle Rechte vorbehalten
        </div>
      </div>
    </div>
  );
};

export default Register;